(() => {
  if (customElements.get('ace-category-directory')) return;
  class ACECategoryDirectory extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.cards = [...this.querySelectorAll('[data-ace-card]')];
      this.grid = this.querySelector('[data-ace-grid]');
      this.search = this.querySelector('[data-ace-search]');
      this.group = this.querySelector('[data-ace-group]');
      this.sort = this.querySelector('[data-ace-sort]');
      this.count = this.querySelector('[data-ace-count]');
      this.pagination = this.querySelector('[data-ace-pagination]');
      this.empty = this.querySelector('[data-ace-empty]');
      this.pageSize = Math.max(1, Math.min(50, Number(this.dataset.pageSize) || 32));
      this.desktopColumns = Math.max(1, Number(this.dataset.desktopColumns) || 8);
      this.loadMore = this.dataset.paginationType === 'load_more';
      this.collator = new Intl.Collator(document.documentElement.lang || 'en', { sensitivity: 'base', numeric: true });
      try { this.recommendations = JSON.parse(this.querySelector('[data-ace-popularity]').textContent); }
      catch { this.recommendations = {}; }
      this.ranks = Object.create(null);
      const categoryHandles = new Set(this.cards.map(card => card.dataset.handle));
      const products = Array.isArray(this.recommendations.products) ? this.recommendations.products : [];
      products.forEach(product => {
        const position = Number(product.position);
        if (!Number.isFinite(position) || position < 1 || position > 50 || !Array.isArray(product.categories)) return;
        // Top positions contribute more; only actual directory categories receive points.
        const weight = 51 - position;
        new Set(product.categories).forEach(handle => {
          if (categoryHandles.has(handle)) this.ranks[handle] = (this.ranks[handle] || 0) + weight;
        });
      });
      this.onPopState = () => { this.readURL(); this.render(false); };
      this.onSearch = () => {
        clearTimeout(this.searchTimer);
        this.searchTimer = setTimeout(() => this.updateFilters('replace'), 180);
      };
      this.onChange = () => this.updateFilters('push');
      this.search.addEventListener('input', this.onSearch);
      this.group.addEventListener('change', this.onChange);
      this.sort.addEventListener('change', this.onChange);
      this.onPageClick = event => {
        const moreButton = event.target.closest('button[data-ace-load-more]');
        if (moreButton) {
          clearTimeout(this.searchTimer);
          this.state.page = Math.min(this.state.page + 1, this.totalPages);
          this.writeURL('push');
          this.render(false, { append: true, focusNew: event.detail === 0 });
          return;
        }
        const link = event.target.closest('a[data-ace-page]');
        if (!link || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey || event.button !== 0) return;
        event.preventDefault();
        this.state.page = Number(link.dataset.acePage);
        this.writeURL('push');
        this.render(true);
      };
      this.pagination.addEventListener('click', this.onPageClick);
      window.addEventListener('popstate', this.onPopState);
      this.querySelector('[data-ace-toolbar]').hidden = false;
      this.readURL();
      this.render(false);
    }
    disconnectedCallback() {
      clearTimeout(this.searchTimer);
      window.removeEventListener('popstate', this.onPopState);
      this.search?.removeEventListener('input', this.onSearch);
      this.group?.removeEventListener('change', this.onChange);
      this.sort?.removeEventListener('change', this.onChange);
      this.pagination?.removeEventListener('click', this.onPageClick);
      this.ready = false;
    }
    readURL() {
      const params = new URL(location.href).searchParams;
      const requestedGroup = params.get('group') || '';
      const rawSort = params.get('sort') || 'recommended';
      const requestedSort = rawSort === 'popular' ? 'recommended' : rawSort;
      this.state = {
        q: (params.get('q') || '').slice(0, 200),
        group: [...this.group.options].some(option => option.value === requestedGroup) ? requestedGroup : '',
        sort: ['recommended', 'az', 'za'].includes(requestedSort) ? requestedSort : 'recommended',
        page: Math.max(1, parseInt(params.get('page'), 10) || 1)
      };
      this.search.value = this.state.q;
      this.group.value = this.state.group;
      this.sort.value = this.state.sort;
    }
    urlFor(page = this.state.page) {
      const url = new URL(location.href);
      for (const [key, value] of Object.entries({ q: this.state.q.trim(), group: this.state.group, sort: this.state.sort })) {
        if (value) url.searchParams.set(key, value);
        else url.searchParams.delete(key);
      }
      if (page > 1) url.searchParams.set('page', String(page));
      else url.searchParams.delete('page');
      return url;
    }
    writeURL(mode) {
      const url = this.urlFor();
      if (url.href === location.href) return;
      history[mode === 'replace' ? 'replaceState' : 'pushState']({}, '', url);
    }
    updateFilters(mode) {
      this.state.q = this.search.value;
      this.state.group = this.group.value;
      this.state.sort = this.sort.value;
      this.state.page = 1;
      this.writeURL(mode);
      this.render(false);
    }
    normalise(value) {
      return value.normalize('NFKD').replace(/\p{Diacritic}/gu, '').toLocaleLowerCase().trim();
    }
    loadImages(card) {
      card.querySelectorAll('img[data-ace-src]').forEach(image => {
        image.srcset = image.dataset.aceSrcset;
        image.src = image.dataset.aceSrc;
        delete image.dataset.aceSrc;
        delete image.dataset.aceSrcset;
      });
    }
    render(moveFocus, options = {}) {
      const query = this.normalise(this.state.q);
      const results = this.cards.filter(card => (!this.state.group || card.dataset.group === this.state.group) && (!query || this.normalise(card.dataset.title).includes(query)));
      results.sort((a, b) => {
        const alpha = this.collator.compare(a.dataset.title, b.dataset.title);
        if (this.state.sort === 'za') return -alpha;
        if (this.state.sort === 'recommended') return (Number(this.ranks[b.dataset.handle]) || 0) - (Number(this.ranks[a.dataset.handle]) || 0) || alpha;
        return alpha;
      });
      const pages = this.loadMore && results.length > this.pageSize
        ? Math.max(2, Math.ceil((results.length - this.desktopColumns) / this.pageSize))
        : Math.max(1, Math.ceil(results.length / this.pageSize));
      this.totalPages = pages;
      const requestedPage = this.state.page;
      this.state.page = Math.min(requestedPage, pages);
      if (this.state.page !== requestedPage) this.writeURL('replace');
      const start = this.loadMore ? 0 : (this.state.page - 1) * this.pageSize;
      let end = this.loadMore ? this.state.page * this.pageSize : start + this.pageSize;
      if (this.loadMore && this.state.page > 1 && results.length - end <= this.desktopColumns) end = results.length;
      const visible = results.slice(start, end);
      const previouslyVisible = new Set(this.cards.filter(card => !card.hidden));
      if (!options.append) {
        this.cards.forEach(card => { card.hidden = true; });
        this.grid.append(...results);
      }
      visible.forEach(card => { card.hidden = false; this.loadImages(card); });
      this.empty.hidden = results.length > 0;
      this.count.textContent = results.length ? `${results.length} Categories · Showing ${start + 1}–${start + visible.length}` : '0 Categories';
      this.renderPagination(pages, results.length, visible.length);
      if (options.focusNew) {
        visible.find(card => !previouslyVisible.has(card))?.querySelector('a[href]')?.focus({ preventScroll: true });
      } else if (moveFocus) {
        this.querySelector('[data-ace-results-heading]').focus({ preventScroll: true });
        this.querySelector('[data-ace-results]').scrollIntoView({ block: 'start', behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
      }
    }
    renderPagination(pages, total, shown) {
      this.pagination.replaceChildren();
      if (this.loadMore) {
        this.pagination.hidden = total === 0;
        if (!total) return;
        const wrapper = document.createElement('div');
        wrapper.className = 'ace-dir-load-more';
        const status = document.createElement('p');
        status.className = 'ace-dir-load-status';
        const categoryLabel = total === 1 ? 'Category' : 'Categories';
        status.textContent = shown >= total ? `All ${total} ${categoryLabel} Shown` : `Showing ${shown} of ${total} ${categoryLabel}`;
        wrapper.append(status);
        if (this.state.page < pages) {
          const button = document.createElement('button');
          button.type = 'button';
          button.className = 'ace-dir-load-button';
          button.dataset.aceLoadMore = '';
          button.textContent = 'Load More';
          button.setAttribute('aria-label', 'Load More Categories');
          if (this.grid.id) button.setAttribute('aria-controls', this.grid.id);
          wrapper.append(button);
        }
        this.pagination.append(wrapper);
        return;
      }
      this.pagination.hidden = pages <= 1;
      if (pages <= 1) return;
      const list = document.createElement('ul');
      const addLink = (page, label, rel) => {
        const item = document.createElement('li');
        const link = document.createElement('a');
        link.href = this.urlFor(page).href;
        link.dataset.acePage = page;
        link.textContent = label;
        link.setAttribute('aria-label', rel ? `${label} Page` : `Page ${page}`);
        if (rel) link.rel = rel;
        item.append(link);
        list.append(item);
      };
      if (this.state.page > 1) addLink(this.state.page - 1, 'Previous', 'prev');
      for (let page = 1; page <= pages; page += 1) {
        if (page === this.state.page) {
          const item = document.createElement('li');
          const current = document.createElement('span');
          current.textContent = page;
          current.setAttribute('aria-current', 'page');
          current.setAttribute('aria-label', `Page ${page}`);
          item.append(current);
          list.append(item);
        } else addLink(page, String(page));
      }
      if (this.state.page < pages) addLink(this.state.page + 1, 'Next', 'next');
      this.pagination.append(list);
    }
  }
  customElements.define('ace-category-directory', ACECategoryDirectory);
})();
